import { t as tr } from '../i18n.js'
import { useEffect, useRef, useState } from 'react'
import { MessageSquare, Paperclip } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type Comment as CommentRow, type CommentRevision, type CommentThread, type ProjectMember } from '../api.js'
import { Markdown } from '../markdown.js'
import { fmt } from './Account.js'
import { attachmentMarkdown, useUpload } from './Attachments.js'

/**
 * Threaded comments (APM-7), one component for all three surfaces: a task, a
 * project's own discussion, a milestone. Threads are one level deep, so a
 * reply is always rendered against its root.
 *
 * Mentions are stored inside the body as `@[Display Name](uuid)`. A reader
 * only ever sees a chip; the composer writes the token when someone is picked
 * out of the @-autocomplete.
 *
 * Files pasted, dropped or picked in the composer upload at once to the same
 * task / project / milestone, and their link goes into the text where the
 * caret was: images as `![name](url)`, which the Markdown renderer shows.
 */
export type CommentEntity = { type: 'task' | 'project' | 'milestone'; id: string }

const urlFor = (projectId: string, e: CommentEntity) =>
  e.type === 'task' ? `/projects/${projectId}/tasks/${e.id}/comments`
    : e.type === 'milestone' ? `/projects/${projectId}/milestones/${e.id}/comments`
      : `/projects/${projectId}/comments`

export function Comments({ projectId, entity, canComment, members, highlight }: {
  projectId: string; entity: CommentEntity; canComment: boolean; members: ProjectMember[]; highlight?: number | undefined
}) {
  const qc = useQueryClient()
  const url = urlFor(projectId, entity)
  const key = ['comments', entity.type, entity.id]
  const q = useQuery({ queryKey: key, queryFn: () => api<{ comments: CommentThread[] }>(url) })
  const [err, setErr] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [replyTo, setReplyTo] = useState<number | null>(null)
  const [flash, setFlash] = useState<number | null>(null)
  const jumped = useRef(false)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])

  const onError = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Request failed')
  const refresh = (notified = 0) => {
    setErr(null)
    void qc.invalidateQueries({ queryKey: key })
    if (notified > 0) {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
      setNotice(notified === 1 ? tr("1 person notified") : tr("{n} people notified", { n: notified }))
      setTimeout(() => setNotice(null), 4000)
    }
  }
  const del = useMutation({
    mutationFn: (id: number) => api<null>(`/projects/${projectId}/comments/${id}`, { method: 'DELETE' }),
    onSuccess: () => refresh(), onError,
  })

  const roots = q.data?.comments ?? []
  const total = roots.reduce((n, c) => n + 1 + c.replies.length, 0)

  // Deep link (?comment=…): once the thread holding it has loaded, scroll to it
  // and flash it. Timers are kept in a ref so a re-render cannot cancel them.
  useEffect(() => {
    const data = q.data
    if (!highlight || jumped.current || !data) return
    if (!data.comments.some((c) => c.id === highlight || c.replies.some((r) => r.id === highlight))) return
    jumped.current = true
    setFlash(highlight)
    timers.current = [
      setTimeout(() => document.getElementById(`comment-${highlight}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80),
      setTimeout(() => setFlash(null), 3000),
    ]
  }, [highlight, q.data])
  useEffect(() => () => { for (const t of timers.current) clearTimeout(t) }, [])

  const shared = { projectId, entity, members, canComment, flash, onRefresh: refresh, onError }
  return (
    <div className="stack comments">
      <div className="row between">
        <strong className="small">{tr("Comments")}{total ? ` · ${total}` : ''}</strong>
        {notice && <span className="small good">{notice}</span>}
      </div>
      {q.isPending && <p className="muted small">{tr("Loading…")}</p>}
      {!q.isPending && roots.length === 0 && <p className="muted small">{tr("No comments yet.")}</p>}
      {roots.map((c) => (
        <div key={c.id}>
          <CommentView {...shared} c={c} canReply={canComment}
            onReply={() => setReplyTo(replyTo === c.id ? null : c.id)}
            onDelete={() => { if (confirm(tr("Delete this comment? Its replies stay."))) del.mutate(c.id) }} />
          {c.replies.map((r) => (
            <CommentView key={r.id} {...shared} c={r} reply canReply={false}
              onDelete={() => { if (confirm(tr("Delete this comment? Its replies stay."))) del.mutate(r.id) }} />
          ))}
          {replyTo === c.id && canComment && (
            <div className="comment reply">
              <Composer url={url} attach={{ projectId, entity }} members={members} replyTo={c.id} autoFocus
                placeholder={tr("Write a reply…")} onError={onError}
                onDone={(n) => { setReplyTo(null); refresh(n) }} onCancel={() => setReplyTo(null)} />
            </div>
          )}
        </div>
      ))}
      {canComment && <Composer url={url} attach={{ projectId, entity }} members={members} placeholder={tr("Write a comment…")} onError={onError} onDone={refresh} />}
      {err && <p className="bad small">{err}</p>}
    </div>
  )
}

function CommentView({ c, reply = false, projectId, entity, members, canComment, canReply, flash, onReply, onDelete, onRefresh, onError }: {
  c: CommentRow; reply?: boolean; projectId: string; entity: CommentEntity; members: ProjectMember[]; canComment: boolean; canReply: boolean
  flash: number | null; onReply?: () => void; onDelete: () => void; onRefresh: (notified?: number) => void; onError: (e: unknown) => void
}) {
  const [editing, setEditing] = useState(false)
  const [history, setHistory] = useState(false)
  const cls = `comment small ${c.system ? 'system' : ''} ${reply ? 'reply' : ''} ${c.deleted ? 'deleted' : ''} ${flash === c.id ? 'flash' : ''}`
  return (
    <div className={cls} id={`comment-${c.id}`}>
      <div className="cmeta muted">
        <span>{c.system ? tr("system") : (c.authorName ?? tr("system"))}</span>
        <span>·</span>
        <span>{fmt(c.createdAt)}</span>
        {c.editedAt && !c.deleted && (
          <button className="link plain small" aria-expanded={history} onClick={() => setHistory((h) => !h)}
            data-tip={tr("Show what this comment said before it was edited")}>{tr("edited")}</button>
        )}
      </div>
      {c.deleted
        ? <div>{c.deletedByName ? tr("Comment deleted by {name}", { name: c.deletedByName }) : tr("Comment deleted")}</div>
        : editing
          ? <Composer url={`/projects/${projectId}/comments/${c.id}`} attach={{ projectId, entity }} method="PATCH" members={members} initial={c.body} autoFocus
              submitLabel={tr("Save")} placeholder={tr("Write a comment…")} onError={onError}
              onDone={(n) => { setEditing(false); onRefresh(n) }} onCancel={() => setEditing(false)} />
          : <Body text={c.body} system={c.system} />}
      {history && <Revisions projectId={projectId} commentId={c.id} />}
      {!c.deleted && !editing && (canReply || c.canEdit || c.canDelete) && (
        <div className="cactions">
          {canReply && canComment && onReply && <button className="link small" data-tip={tr("Reply in this thread")} onClick={onReply}>{tr("Reply")}</button>}
          {c.canEdit && <button className="link small" data-tip={tr("Edit your comment; the previous text is kept as history")} onClick={() => setEditing(true)}>{tr("Edit")}</button>}
          {c.canDelete && <button className="link small danger" data-tip={tr("Remove this comment; its replies stay")} onClick={onDelete}>{tr("Delete")}</button>}
        </div>
      )}
    </div>
  )
}

/** Markdown, with `@[Name](uuid)` turned into chips (APM-10). System notes are fixed strings, so they keep going through tr() verbatim. */
function Body({ text, system = false }: { text: string; system?: boolean }) {
  if (system) return <div style={{ whiteSpace: 'pre-wrap' }}>{tr(text)}</div>
  return <Markdown text={text} />
}

/** Earlier versions of a comment, oldest first; each row is the text as it was before that edit. */
function Revisions({ projectId, commentId }: { projectId: string; commentId: number }) {
  const q = useQuery({ queryKey: ['comment-revisions', commentId], queryFn: () => api<{ revisions: CommentRevision[] }>(`/projects/${projectId}/comments/${commentId}/revisions`) })
  const rows = q.data?.revisions ?? []
  return (
    <div className="revisions small muted">
      {q.isPending && <p className="muted small">{tr("Loading…")}</p>}
      {!q.isPending && rows.length === 0 && <p className="muted small">{tr("No earlier versions.")}</p>}
      {rows.map((r) => (
        <div key={r.id}>
          <div className="muted">{fmt(r.editedAt)} — {tr("edited by")} {r.editedByName ?? tr("system")}</div>
          <Body text={r.body} />
        </div>
      ))}
    </div>
  )
}

/** Post a new comment or save an edit. Ctrl/Cmd-Enter submits; @ opens the people picker; Preview renders the Markdown. */
function Composer({ url, attach, method = 'POST', members, replyTo, initial = '', placeholder, submitLabel, autoFocus = false, onDone, onCancel, onError }: {
  url: string; attach: { projectId: string; entity: CommentEntity }; method?: 'POST' | 'PATCH'; members: ProjectMember[]; replyTo?: number; initial?: string
  placeholder: string; submitLabel?: string; autoFocus?: boolean
  onDone: (notified: number) => void; onCancel?: () => void; onError: (e: unknown) => void
}) {
  const [body, setBody] = useState(initial)
  const [preview, setPreview] = useState(false)
  const send = useMutation({
    mutationFn: () => api<{ notified: number }>(url, { method, json: replyTo === undefined ? { body } : { body, replyTo } }),
    onSuccess: (r) => { setBody(''); setPreview(false); onDone(r.notified) }, onError,
  })
  const { upload, busy } = useUpload(attach.projectId, attach.entity, onError)
  const form = useRef<HTMLFormElement>(null)
  const picker = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  /** Upload, then put each file's link where the caret was when the files arrived. */
  const addFiles = async (files: File[]) => {
    if (!files.length) return
    const box = form.current?.querySelector('textarea')
    const at = box ? box.selectionStart : body.length
    const done = await upload(files)
    if (!done.length) return
    const md = done.map(attachmentMarkdown).join('\n')
    setBody((b) => {
      const pos = Math.min(at, b.length), before = b.slice(0, pos)
      return `${before}${before && !before.endsWith('\n') ? '\n' : ''}${md}\n${b.slice(pos)}`
    })
  }
  const submit = () => { if (body.trim() && !send.isPending && busy === 0) send.mutate() }
  return (
    <form ref={form} className={`stack composer ${over ? 'dropover' : ''}`} onSubmit={(e) => { e.preventDefault(); submit() }}
      onPaste={(e) => { const f = Array.from(e.clipboardData.files); if (f.length) { e.preventDefault(); void addFiles(f) } }}
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); e.stopPropagation(); setOver(true) } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false) }}
      onDrop={(e) => { if (e.dataTransfer.files.length) { e.preventDefault(); e.stopPropagation(); setOver(false); void addFiles(Array.from(e.dataTransfer.files)) } }}>
      {preview
        ? <div className="cpreview">{body.trim() ? <Markdown text={body} /> : <p className="muted small">{tr("Nothing to preview yet.")}</p>}</div>
        : <MentionBox value={body} onChange={setBody} members={members} placeholder={placeholder} autoFocus={autoFocus} onSubmit={submit} />}
      <div className="row">
        <button className="button sm" disabled={!body.trim() || send.isPending || busy > 0} data-tip={tr("Post the comment (Ctrl-Enter)")}>{submitLabel ?? tr("Comment")}</button>
        <button type="button" className="button sm" aria-label={tr("Attach files")} data-tip={tr("Attach files to the comment; you can also paste or drop them into it")}
          onClick={() => picker.current?.click()}><Paperclip size={14} strokeWidth={2} aria-hidden /></button>
        <input ref={picker} type="file" multiple hidden onChange={(e) => { void addFiles(Array.from(e.target.files ?? [])); e.target.value = '' }} />
        <button type="button" className="button sm" aria-pressed={preview} onClick={() => setPreview((p) => !p)}
          data-tip={tr("Show the comment formatted, as it will appear")}>{preview ? tr("Write") : tr("Preview")}</button>
        {onCancel && <button type="button" className="button sm" onClick={onCancel}>{tr("Cancel")}</button>}
        {busy > 0
          ? <span className="small muted">{busy === 1 ? tr("Uploading 1 file…") : tr("Uploading {n} files…", { n: busy })}</span>
          : <span className="small muted">{tr("Markdown · type @ to mention someone · Ctrl-Enter posts")}</span>}
      </div>
    </form>
  )
}

/**
 * A textarea with @-mention autocomplete. The fragment is the text between an
 * `@` at the start of a line or after whitespace and the caret; picking a
 * person swaps it for the `@[Name](uuid)` token the API expects.
 */
function MentionBox({ value, onChange, members, placeholder, autoFocus, onSubmit }: {
  value: string; onChange: (v: string) => void; members: ProjectMember[]
  placeholder: string; autoFocus?: boolean; onSubmit: () => void
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const [pop, setPop] = useState<{ from: number; to: number; q: string } | null>(null)
  const [idx, setIdx] = useState(0)
  const caret = useRef<number | null>(null)

  useEffect(() => { if (caret.current !== null && ref.current) { ref.current.selectionStart = ref.current.selectionEnd = caret.current; caret.current = null } })

  const needle = (pop?.q ?? '').toLowerCase()
  const matches = pop ? members.filter((m) => m.displayName.toLowerCase().includes(needle)).slice(0, 8) : []
  const open = matches.length > 0

  const scan = (text: string, at: number) => {
    const m = /(?:^|\s)@([^\n@]{0,40})$/.exec(text.slice(0, at))
    setPop(m ? { from: at - (m[1] ?? '').length - 1, to: at, q: m[1] ?? '' } : null)
    setIdx(0)
  }
  const pick = (m: ProjectMember) => {
    if (!pop) return
    const token = `@[${m.displayName}](${m.userId}) `
    onChange(value.slice(0, pop.from) + token + value.slice(pop.to))
    caret.current = pop.from + token.length
    setPop(null)
  }
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(matches.length - 1, i + 1)); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); return }
      if (e.key === 'Enter' || e.key === 'Tab') { const m = matches[idx]; if (m) { e.preventDefault(); pick(m) } return }
      // Without stopPropagation this Escape also closes the command palette.
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setPop(null); return }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSubmit() }
  }
  return (
    <div className="mentionwrap">
      <textarea ref={ref} rows={3} maxLength={10000} value={value} placeholder={placeholder} autoFocus={autoFocus}
        aria-label={placeholder} onKeyDown={onKeyDown} onBlur={() => setTimeout(() => setPop(null), 120)}
        onChange={(e) => { onChange(e.target.value); scan(e.target.value, e.target.selectionStart) }}
        onClick={(e) => scan(value, e.currentTarget.selectionStart)} />
      {open && (
        <ul className="mentionpop" aria-label={tr("Mention someone")}>
          {matches.map((m, i) => (
            <li key={m.userId}>
              <button type="button" className={i === idx ? 'active' : ''} aria-current={i === idx}
                onMouseEnter={() => setIdx(i)} onMouseDown={(e) => { e.preventDefault(); pick(m) }}>
                {m.displayName}<span className="muted small"> · {m.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The button a milestone row uses to open its thread. */
export function CommentsToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button className="link plain" aria-expanded={open} aria-label={tr("Discussion")}
      data-tip={open ? tr("Hide this milestone's discussion") : tr("Discuss this milestone")} onClick={onClick}>
      <MessageSquare size={14} strokeWidth={2} aria-hidden />
    </button>
  )
}
